import React, { useState } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import bcrypt from "bcryptjs";
import { FaUserAlt, FaLock, FaEye, FaEyeSlash, FaGem } from "react-icons/fa";
import "./RetailerLogin.scss";

const RetailerLogin = ({ onLoginSuccess }) => {
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
    if (error) setError(""); // Clear error when user types
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!credentials.username || !credentials.password) {
      setError("Please enter both username and password.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // 1. Find the Retailer by Username
      const q = query(
        collection(db, "parties"),
        where("username", "==", credentials.username.trim()),
        where("type", "==", "Retailer"),
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError("Invalid username or password.");
        setIsLoading(false);
        return;
      }

      // 2. Verify Password
      const retailerDoc = querySnapshot.docs[0];
      const retailerData = retailerDoc.data();

      // Check status
      if (retailerData.status === "Inactive") {
        setError("Your account is suspended. Please contact admin.");
        setIsLoading(false);
        return;
      }

      // Compare hashed password
      const isMatch = bcrypt.compareSync(
        credentials.password,
        retailerData.password,
      );

      if (!isMatch) {
        setError("Invalid username or password.");
        setIsLoading(false);
        return;
      }

      // 3. Success! Pass user data up to App state/Context
      const sessionData = { id: retailerDoc.id, ...retailerData };
      delete sessionData.password; // Never store hash in local storage
      localStorage.setItem("retailerSession", JSON.stringify(sessionData));

      onLoginSuccess(sessionData);
    } catch (err) {
      console.error("Login Error Detailed:", err);
      if (err.code === "permission-denied") {
        setError("Permission Denied: Security Rules blocking access.");
      } else if (err.message) {
        setError(`Error: ${err.message}`);
      } else {
        setError("Network error. Please check your connection.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="v-rt-login-wrapper">
      {/* Aesthetic Ambient Glows */}
      <div className="v-rt-login-glow v-rt-login-glow-top"></div>
      <div className="v-rt-login-glow v-rt-login-glow-bottom"></div>

      <div className="v-rt-login-container">
        <div className="v-rt-login-header">
          <div className="v-rt-login-logo-box">
            <FaGem className="v-rt-login-logo-icon" />
          </div>
          <h1 className="v-rt-login-brand">Vaidya Jewellers</h1>
          <span className="v-rt-login-sub-brand">by Ashish</span>
          <p className="v-rt-login-subtitle">B2B Retailer Portal</p>
        </div>

        <form className="v-rt-login-form" onSubmit={handleLogin}>
          <div className="v-rt-login-input-group">
            <div className="v-rt-login-icon-wrap">
              <FaUserAlt />
            </div>
            <input
              type="text"
              name="username"
              placeholder="Username"
              className="v-rt-login-input"
              value={credentials.username}
              onChange={handleChange}
              autoCapitalize="none"
              autoComplete="username"
              disabled={isLoading}
            />
          </div>

          <div className="v-rt-login-input-group">
            <div className="v-rt-login-icon-wrap">
              <FaLock />
            </div>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder="Password"
              className="v-rt-login-input"
              value={credentials.password}
              onChange={handleChange}
              autoComplete="current-password"
              disabled={isLoading}
            />
            <button
              type="button"
              className="v-rt-login-toggle-password"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex="-1"
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {error && (
            <div className="v-rt-login-error shake-animation">{error}</div>
          )}

          <button
            type="submit"
            className={`v-rt-login-btn ${isLoading ? "loading" : ""}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="v-rt-login-spinner"></div>
            ) : (
              "Secure Login"
            )}
          </button>
        </form>

        <div className="v-rt-login-footer">
          Having trouble?{" "}
          <a href="https://wa.me/8655981117" target="_blank" rel="noreferrer">
            Contact Admin
          </a>
        </div>
      </div>
    </div>
  );
};

export default RetailerLogin;
